import runtimeConfigLoader from '@/runtime-config-loader';

type FacebookSdk = {
    init(config: {
        appId: string;
        autoLogAppEvents: boolean;
        xfbml: boolean;
        version: string;
    }): void;
    login(callback: (response: fb.StatusResponse) => void, options?: { scope?: string }): void;
};

let facebookSdkPromise: Promise<FacebookSdk> | null = null;

const loadScript = () => new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById('facebook-jssdk') as HTMLScriptElement | null;
    if (existingScript) {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Facebook SDK failed to load.')), { once: true });
        return;
    }

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.async = true;
    script.defer = true;
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Facebook SDK failed to load.')), { once: true });
    document.body.appendChild(script);
});

export default async function ensureFacebookSdk(): Promise<FacebookSdk> {
    const existingSdk = (window as Window & { FB?: FacebookSdk }).FB;
    if (existingSdk) {
        return existingSdk;
    }

    if (!facebookSdkPromise) {
        facebookSdkPromise = (async () => {
            const { facebookAppId } = await runtimeConfigLoader;
            await loadScript();
            const facebookSdk = (window as Window & { FB?: FacebookSdk }).FB;

            if (!facebookSdk) {
                throw new Error('Facebook SDK is unavailable after script load.');
            }

            facebookSdk.init({
                appId: facebookAppId,
                autoLogAppEvents: true,
                xfbml: true,
                version: 'v5.0',
            });

            return facebookSdk;
        })().catch((error) => {
            facebookSdkPromise = null;
            throw error;
        });
    }

    return facebookSdkPromise;
}
